/** 選べる再生速度（FR-SIM-002）。 */
export const PLAYBACK_RATES = [0.25, 0.5, 1, 2, 4] as const;

/** 再生速度。 */
export type PlaybackRate = (typeof PLAYBACK_RATES)[number];

/** 再生の状態（UI に見せる分）。時刻は 0.1 秒に丸める。 */
export interface PlaybackSnapshot {
  /** 再生位置 [s]（0.1 秒刻み） */
  readonly time: number;
  /** 結果全体の長さ [s] */
  readonly duration: number;
  readonly playing: boolean;
  readonly rate: PlaybackRate;
  /** 最後まで再生して止まっているか */
  readonly ended: boolean;
}

/**
 * シミュレーション結果の再生位置を管理する（FR-SIM-001, 002）。
 * 時刻は {@link advance} で進める（描画ループから呼ぶ）。DOM・React に依存しない。
 */
export class PlaybackController {
  private currentTime = 0;
  private isPlaying: boolean;
  private currentRate: PlaybackRate = 1;
  private isEnded = false;
  private snapshot: PlaybackSnapshot;
  private readonly listeners = new Set<() => void>();

  /**
   * @param duration 結果全体の長さ [s]（0 以上）
   * @param autoplay 作った直後から再生するか（既定 `true`）
   */
  constructor(
    private duration: number,
    autoplay = true,
  ) {
    if (!(duration >= 0) || !Number.isFinite(duration)) {
      throw new RangeError('再生の長さは 0 以上の有限な値にしてください');
    }
    this.isPlaying = autoplay && duration > 0;
    this.snapshot = this.makeSnapshot();
  }

  /** 現在の再生位置 [s]（丸めない。描画に使う）。 */
  get time(): number {
    return this.currentTime;
  }

  /** UI 用の状態。変化があるまで同じオブジェクトを返す（`useSyncExternalStore` 向け）。 */
  getSnapshot = (): PlaybackSnapshot => this.snapshot;

  /** 状態の変化を購読する。解除関数を返す。 */
  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  };

  /** 再生する。最後まで再生して止まっていた場合は、最初から再生する。 */
  play(): void {
    if (this.duration <= 0) return;
    if (this.isEnded) {
      this.currentTime = 0;
      this.isEnded = false;
    }
    this.isPlaying = true;
    this.publish();
  }

  /** 一時停止する（位置は保つ）。 */
  pause(): void {
    this.isPlaying = false;
    this.publish();
  }

  /** 再生中なら一時停止、停止中なら再生する。 */
  toggle(): void {
    if (this.isPlaying) this.pause();
    else this.play();
  }

  /** 停止する（最初の位置へ戻す）。 */
  stop(): void {
    this.isPlaying = false;
    this.isEnded = false;
    this.currentTime = 0;
    this.publish();
  }

  /** 再生速度を変える。 */
  setRate(rate: PlaybackRate): void {
    this.currentRate = rate;
    this.publish();
  }

  /** 再生位置を移す（範囲外は端に丸める）。 */
  seek(time: number): void {
    this.currentTime = Math.min(this.duration, Math.max(0, time));
    this.isEnded = false;
    this.publish();
  }

  /**
   * 結果全体の長さを変える（走行中の操作で、走行を計算し直したとき）。再生位置・速度・再生中かは保つ。
   * 新しい長さより先にいれば、最後の位置で止まる。最後まで再生して止まっていたが、長さが延びたときは、
   * 続きを再生する（先の一部だけを計算した仮の結果の終わりに、追いついていた場合）。
   */
  setDuration(duration: number): void {
    if (!(duration >= 0) || !Number.isFinite(duration)) {
      throw new RangeError('再生の長さは 0 以上の有限な値にしてください');
    }
    this.duration = duration;
    if (this.currentTime >= duration) {
      this.currentTime = duration;
      if (this.isPlaying) {
        this.isPlaying = false;
        this.isEnded = true;
      }
    } else if (this.isEnded) {
      this.isEnded = false;
      this.isPlaying = true;
    }
    this.publish(true);
  }

  /**
   * 実時間 `deltaSeconds` 秒分だけ再生位置を進める（再生中のみ。再生速度を掛ける）。
   * 最後に達したら、最後の位置で止まる。
   */
  advance(deltaSeconds: number): void {
    if (!this.isPlaying || !(deltaSeconds > 0)) return;
    this.currentTime += deltaSeconds * this.currentRate;
    if (this.currentTime >= this.duration) {
      this.currentTime = this.duration;
      this.isPlaying = false;
      this.isEnded = true;
    }
    this.publish();
  }

  private makeSnapshot(): PlaybackSnapshot {
    return {
      time: Math.round(this.currentTime * 10) / 10,
      duration: this.duration,
      playing: this.isPlaying,
      rate: this.currentRate,
      ended: this.isEnded,
    };
  }

  /** 見える状態が変わったときだけ、購読者へ知らせる。 */
  private publish(force = false): void {
    const next = this.makeSnapshot();
    const prev = this.snapshot;
    if (
      !force &&
      next.time === prev.time &&
      next.playing === prev.playing &&
      next.rate === prev.rate &&
      next.ended === prev.ended
    ) {
      return;
    }
    this.snapshot = next;
    for (const listener of this.listeners) listener();
  }
}
